import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import type { RootState } from '../store';
import { useAuth } from '../hooks/useAuth';

interface PrivateRouteProps {
  children: ReactNode;
}

const LOGIN_ROUTE = '/login';
const AUTH_PREFIX = '/auth/';

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, user } = useSelector((state: RootState) => (state as any).auth);
  const { checkAuth, isLoading } = useAuth();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const path = location.pathname;
  const isCallbackRoute = path.includes(AUTH_PREFIX);
  const isLoginRoute = path.includes('/login');
  const search = location.search || '';
  const disableGuard =
    search.includes('authDebug=1') ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('disable_auth_guard') === 'true');

  useEffect(() => {
    if (!hasCheckedAuth && !isLoginRoute && !isCallbackRoute) {
      checkAuth().finally(() => setHasCheckedAuth(true));
    } else if (!hasCheckedAuth) {
      setHasCheckedAuth(true);
    }
  }, [hasCheckedAuth, checkAuth, isLoginRoute, isCallbackRoute]);

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[PrivateRoute]', {
      path,
      isAuthenticated,
      isLoginRoute,
      isCallbackRoute,
      roles: user?.roles,
      disableGuard,
    });
  }

  if (disableGuard) {
    return <>{children}</>;
  }

  if (isLoginRoute || isCallbackRoute) {
    return <>{children}</>;
  }

  if (!hasCheckedAuth || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(path + location.search);
    if (typeof window !== 'undefined') {
      window.location.href = `${LOGIN_ROUTE}?returnUrl=${returnUrl}`;
      return null;
    }
    return <Navigate to={`${LOGIN_ROUTE}?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;

